import { COMPONENT_TYPES } from '../../../constants';

/**
 * 디바이스 속성을 처리하는 유틸리티 함수
 * 디바이스 타입에 따라 적절한 속성을 병합합니다.
 */
export const applyDeviceProperties = (component: any, baseProps: any, deviceType = 'pc') => {
  const deviceProps = { ...baseProps };

  // 디바이스별 스타일 적용
  if (component.style?.deviceProperty && component.style.deviceProperty[deviceType]) {
    deviceProps.style = {
      ...deviceProps.style,
      ...component.style.deviceProperty[deviceType]
    };
  }

  // 디바이스별 componentProps 적용
  if (component.componentProps?.deviceProperty && component.componentProps.deviceProperty[deviceType]) {
    deviceProps.componentProps = {
      ...deviceProps.componentProps,
      ...component.componentProps.deviceProperty[deviceType]
    };
  }

  // 디바이스별 data 적용
  if (component.data?.deviceProperty && component.data.deviceProperty[deviceType]) {
    deviceProps.data = {
      ...deviceProps.data,
      ...component.data.deviceProperty[deviceType]
    };
  }

  // 디바이스별 content 적용
  if (component.content?.deviceProperty && component.content.deviceProperty[deviceType]) {
    // content가 객체인 경우
    if (typeof component.content === 'object') {
      deviceProps.content = {
        ...deviceProps.content,
        ...component.content.deviceProperty[deviceType]
      };
    } 
    // content가 문자열인 경우
    else if (typeof component.content.deviceProperty[deviceType] !== 'undefined') {
      deviceProps.content = component.content.deviceProperty[deviceType];
    }
  }

  return deviceProps;
};

/**
 * 기본 props 생성 함수
 */
export const createBaseProps = (component: any, deviceType = 'pc') => {
  // 기본 props 생성
  const baseProps = {
    data: component.data || {},
    content: component.content,
    style: component.style || {},
    componentProps: component.componentProps || {},
    viewMode: deviceType,
    // 컴포넌트의 최상위 속성도 props에 포함 (CartComponent 등에서 사용)
    title: component.title,
    emptyText: component.emptyText,
    shippingFee: component.shippingFee,
    showStock: component.showStock
  };

  // 디바이스별 속성 적용
  return applyDeviceProperties(component, baseProps, deviceType);
};

/**
 * USER_PROFILE 컴포넌트의 props 생성
 */
export const createUserProfileProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const varFields = component.varFields || 
                  component.componentProps?.varFields || {
    var01: { show: false, label: "" },
    var02: { show: false, label: "" },
    var03: { show: false, label: "" }
  };
  
  // basicFields 처리
  const basicFields = component.basicFields || 
                    component.componentProps?.basicFields || {
    userId: true,
    name: true,
    password: true,
    phone: true,
    email: true,
    birthday: true,
    createdAt: true,
    address: true,
    referralLink: true
  };
  
  const title = component.content?.title || 
              component.componentProps?.title || 
              '회원 정보';
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: { 
      ...baseProps.content,
      title: title
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      varFields: varFields,
      basicFields: basicFields
    },
    varFields: varFields,
    basicFields: basicFields
  };
};

/**
 * SIGNUP 컴포넌트의 props 생성
 */
export const createSignupProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const varFields = component.varFields || 
                  component.componentProps?.varFields || {
    var01: { show: false, label: "" },
    var02: { show: false, label: "" },
    var03: { show: false, label: "" }
  };
  
  // basicFields 처리
  const basicFields = component.basicFields || 
                    component.componentProps?.basicFields || {
    userId: true,
    password: true,
    name: true,
    phone: true,
    email: true,
    birthday: true,
    address: true,
    referralCode: true
  };
  
  const title = component.content?.title || 
              component.componentProps?.title || 
              '회원 가입';
  
  const buttonText = component.content?.buttonText || 
                   component.componentProps?.buttonText || 
                   '가입하기';
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: { 
      ...baseProps.content,
      title: title,
      buttonText: buttonText
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      buttonText: buttonText,
      varFields: varFields,
      basicFields: basicFields
    },
    varFields: varFields,
    basicFields: basicFields
  };
};

/**
 * MEMBERSHIP_SIGNUP 컴포넌트의 props 생성
 */
export const createMembershipSignupProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const title = component.componentProps?.title || 
              component.content?.title || 
              "멤버십 가입신청";
  
  const description = component.componentProps?.description || 
                    component.content?.description || 
                    "멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다.";
  
  const buttonText = component.componentProps?.buttonText || 
                   component.content?.buttonText || 
                   "가입 신청";
  
  const completedText = component.componentProps?.completedText || 
                      component.content?.completedText || 
                      "가입 완료";
  
  const productId = Number(component.componentProps?.productId) || 
                  Number(component.content?.productId) || 
                  0;
  
  // 수정된 props 구성
  return {
    ...baseProps,
    content: {
      ...baseProps.content,
      title: title,
      description: description,
      buttonText: buttonText,
      completedText: completedText,
      productId: productId
    },
    componentProps: { 
      ...baseProps.componentProps,
      className: baseProps.componentProps?.className || "membership-signup-wrapper",
      title: title,
      productId: productId,
      buttonText: buttonText,
      completedText: completedText,
      description: description
    },
    isPreview: false
  };
};

/**
 * FAQ 컴포넌트의 props 생성
 */
export const createFAQProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 특수 속성 처리
  const faqItems = component.faqItems || 
                 component.content?.faqItems || 
                 component.componentProps?.faqItems ||
                 [];
  
  const title = component.title || 
              component.content?.title || 
              component.componentProps?.title ||
              "자주하는질문";
  
  const titleStyle = component.titleStyle || 
                   component.style?.titleStyle || 
                   component.componentProps?.titleStyle ||
                   {};
  
  // 수정된 props 구성
  return {
    ...baseProps,
    id: component.id,
    title: title,
    faqItems: faqItems,
    titleStyle: titleStyle,
    content: { 
      ...baseProps.content,
      title: title,
      faqItems: faqItems
    },
    componentProps: {
      ...baseProps.componentProps,
      title: title,
      faqItems: faqItems,
      titleStyle: titleStyle
    }
  };
};

/**
 * BOARD 컴포넌트의 props 생성
 */
export const createBoardProps = (component: any, deviceType = 'pc') => {
  const baseProps = createBaseProps(component, deviceType);
  
  // 버튼 표시 설정 추출
  const showNewButton = component.showNewButton !== undefined ? component.showNewButton :
                       component.componentProps?.showNewButton !== undefined ? component.componentProps.showNewButton :
                       component.data?.showNewButton !== undefined ? component.data.showNewButton : true;
  
  const showDetailButtons = component.showDetailButtons !== undefined ? component.showDetailButtons :
                          component.componentProps?.showDetailButtons !== undefined ? component.componentProps.showDetailButtons :
                          component.data?.showDetailButtons !== undefined ? component.data.showDetailButtons : true;
  
  // 디바이스별 설정 확인
  let deviceShowNewButton = true;
  
  if (component.data?.device?.[deviceType]?.showNewButton !== undefined) {
    deviceShowNewButton = component.data.device[deviceType].showNewButton;
  } else if (component.componentProps?.device?.[deviceType]?.showNewButton !== undefined) {
    deviceShowNewButton = component.componentProps.device[deviceType].showNewButton;
  }
  
  // 데이터 구성
  const boardData = {
    ...(baseProps.data || {}),
    showNewButton: deviceType === 'pc' ? showNewButton : deviceShowNewButton,
    showDetailButtons: showDetailButtons,
    device: {
      ...(baseProps.data?.device || {}),
      pc: {
        ...(baseProps.data?.device?.pc || {}),
        showNewButton: component.data?.device?.pc?.showNewButton !== undefined ? 
                       component.data.device.pc.showNewButton : showNewButton
      },
      mobile: {
        ...(baseProps.data?.device?.mobile || {}),
        showNewButton: component.data?.device?.mobile?.showNewButton !== undefined ? 
                       component.data.device.mobile.showNewButton : deviceShowNewButton
      }
    }
  };
  

  
  return {
    ...baseProps,
    data: boardData
  };
};

/**
 * 컴포넌트 타입별 특수 props 생성
 */
export const createComponentProps = (component: any, deviceType = 'pc') => {
  // 타입 표준화
  const typeStr = String(component.type).toUpperCase();
  
  // 컴포넌트 타입별 특수 처리
  switch (typeStr) {
    case COMPONENT_TYPES.USER_PROFILE:
    case 'USER_PROFILE':
      return createUserProfileProps(component, deviceType);
      
    case COMPONENT_TYPES.SIGNUP:
    case 'SIGNUP':
      return createSignupProps(component, deviceType);
      
    case COMPONENT_TYPES.MEMBERSHIP_SIGNUP:
    case 'MEMBERSHIP_SIGNUP':
      return createMembershipSignupProps(component, deviceType);
      
    case COMPONENT_TYPES.FAQ:
    case 'FAQ':
      return createFAQProps(component, deviceType);
    
    case COMPONENT_TYPES.BOARD:
    case 'BOARD':
      return createBoardProps(component, deviceType);
      
    // 다른 특수 컴포넌트 추가 가능
      
    default:
      // 기본 props 생성
      return createBaseProps(component, deviceType);
  }
}; 